// src/shared/combobox.js — търсачка вместо падащ списък (ПРОМТ-13б).
//
// Падащ списък със 170 артикула във всеки от 302 реда е и тежък (страницата
// носеше 50 000 <option>), и неизползваем: човек не скролира 170 реда, за да
// намери „Кока-Кола". Тук се пише част от името или кода и излизат до 20
// резултата. Списъкът (items) е ЕДИН за екрана и се подава наготово —
// компонентът не тегли нищо и не държи копие във всеки ред; опциите се
// чертаят едва при отваряне.
//
// Достъпност (WAI-ARIA combobox с listbox): полето е role="combobox" с
// aria-expanded / aria-controls / aria-activedescendant; стрелките местят
// избора, Enter избира, Esc затваря. Етикетът идва отвън — през field()
// (намира полето в обвивката) или с ariaLabel за поле в ред на таблица.
//
// Правилото за избора: изписан текст НЕ е избор. Ако човек напише нещо и
// излезе, без да избере ред, полето се връща към последния истински избор —
// няма състояние „на екрана пише едно, а е записано друго". Изтрито поле =
// изчистен избор.

import { el, clear } from './dom.js';

let seq = 0;
const lower = (s) => String(s == null ? '' : s).toLowerCase();

// opts:
//   items       — масив или функция, която го връща (общият списък на екрана)
//   label(it)   — текстът на реда и на полето след избор
//   sub(it)     — втори, по-дребен ред (напр. „ЕИК: …"), по избор
//   search(it)  — по какво се търси (по подразбиране label + sub)
//   idOf(it)    — ключът (по подразбиране it.id)
//   value       — началният избор (ключ) или null
//   onPick(it)  — вика се при избор; null при изчистване
//   footer      — { label(q), onPick(q) }: последен ред „+ Нов … с това име"
//   placeholder, ariaLabel, emptyText, limit (20), disabled
//   inline      — списъкът е в потока под полето, не изскача над страницата
//   autoPickSingle — при излизане с изписан текст и ЕДНО съвпадение то се
//                 избира само (клиентът във фактурата, ПРОМТ-19)
//   footerDefault — без съвпадение редът footer е избран, тоест Enter го
//                 натиска (иначе Enter без съвпадение не прави нищо)
//   onBlurUnmatched(text) — излизане с текст, който не съвпада с нищо (напр.
//                 ЕИК на непознат клиент → регистърът)
//   match(it, words) — свое правило за съвпадение (по подразбиране всяка дума
//                 е част от search(it))
//   sections(found, q) — резултатите на групи със заглавия: [{ label, items }]
//                 (категориите на разходите, ПРОМТ-46); празните не се чертаят
//   side(it)    — дребен сив текст вдясно на реда (напр. сметката „602")
//   className   — допълнителен клас на обвивката (за CSS на конкретно поле)
//   mobileFull  — под 768 px отвореният списък е на цял екран, полето е
//                 най-отгоре, с бутон „Затвори" (CSS: .combo-full)
//   float       — списъкът „плава" над страницата (position: fixed до полето):
//                 в таблица с плъзгач (редовете на доставката) обикновеният
//                 се отрязваше и се виждаше само първото заглавие (Макс,
//                 02.11.2026: „много е смачкано и не се вижда")
export function combobox(opts) {
  const o = opts || {};
  const limit = Number(o.limit) > 0 ? Number(o.limit) : 20;
  const idOf = o.idOf || ((it) => it.id);
  const labelOf = o.label || ((it) => String(it.name || ''));
  const subOf = o.sub || (() => '');
  const searchOf = o.search || ((it) => labelOf(it) + ' ' + subOf(it));
  const list = () => (typeof o.items === 'function' ? o.items() : o.items) || [];
  const uid = 'cbx-' + (++seq);

  const inp = el('input', {
    type: 'text', class: 'combo-input', role: 'combobox', autocomplete: 'off',
    'aria-autocomplete': 'list', 'aria-expanded': 'false',
    placeholder: o.placeholder || 'Пишете, за да търсите…',
    'aria-label': o.ariaLabel || null,
  });
  // inline: списъкът стои В потока под полето (за малък диалог, където
  // изскачащ списък би се отрязал от ръба на диалога).
  const root = el('div', { class: 'combo' + (o.inline ? ' combo-inline' : '') + (o.mobileFull ? ' combo-full' : '') + (o.className ? ' ' + o.className : '') }, inp);
  // Списъкът се създава едва при първото отваряне: в таблица с 300 реда
  // затвореното поле е само един <input>, не поле + празна кутия.
  let box = null;
  function ensureBox() {
    if (box) return;
    // mousedown навсякъде в списъка (и по плъзгача му) не взима фокуса от
    // полето — иначе клик по „Още N…" или скрол затваряше списъка.
    box = el('div', { class: 'combo-list', role: 'listbox', id: uid + '-list', 'aria-label': 'Резултати', onMousedown: (e) => e.preventDefault() });
    // На цял екран (телефон) няма „извън полето", където да се докоснеш —
    // бутонът взима фокуса, полето губи своя и списъкът се затваря.
    if (o.mobileFull) root.appendChild(el('button', { type: 'button', class: 'btn sm ghost combo-close', text: 'Затвори' }));
    root.appendChild(box);
    inp.setAttribute('aria-controls', box.id);
  }

  let picked = null;   // избраният елемент (или null)
  let shown = [];      // това, което е начертано: [{ item } | { footer: true }]
  let active = -1;
  let open = false;

  function find(id) {
    if (id == null || id === '') return null;
    return list().find((it) => String(idOf(it)) === String(id)) || null;
  }
  function showPicked() { inp.value = picked ? labelOf(picked) : ''; }

  function matches(q) {
    const words = lower(q).trim().split(/\s+/).filter(Boolean);
    const all = list();
    if (!words.length) return all;
    if (o.match) return all.filter((it) => o.match(it, words));
    return all.filter((it) => { const hay = lower(searchOf(it)); return words.every((w) => hay.includes(w)); });
  }

  function setActive(i) {
    active = i;
    if (!box) return;
    [...box.querySelectorAll('[role=option]')].forEach((n, k) => {
      const on = k === i;
      n.classList.toggle('active', on);
      n.setAttribute('aria-selected', on ? 'true' : 'false');
      if (on && n.scrollIntoView) n.scrollIntoView({ block: 'nearest' });
    });
    if (i >= 0) inp.setAttribute('aria-activedescendant', uid + '-o' + i);
    else inp.removeAttribute('aria-activedescendant');
  }

  function render() {
    ensureBox();
    clear(box);
    // При отворено поле с вече избран ред се показва целият списък, не само
    // „съвпаденията" със собственото име на избора.
    const q = picked && inp.value === labelOf(picked) ? '' : inp.value;
    const found = matches(q);
    const items = found.slice(0, limit);
    shown = [];
    const opt = (it) => {
      const i = shown.length;
      shown.push({ item: it });
      const sub = subOf(it);
      const side = o.side ? o.side(it) : '';
      const text = [el('span', { class: 'combo-opt-main', text: labelOf(it) }), sub ? el('span', { class: 'combo-opt-sub', text: sub }) : null];
      return el('div', {
        class: 'combo-opt' + (side ? ' combo-opt-row' : ''), role: 'option', id: uid + '-o' + i, 'aria-selected': 'false',
        // mousedown, не click: полето не бива да губи фокуса преди избора.
        onMousedown: (e) => { e.preventDefault(); choose(i); },
      }, ...(side ? [el('span', { class: 'combo-opt-text' }, ...text), el('span', { class: 'combo-opt-side', text: side })] : text));
    };
    if (o.sections) {
      // Групите: заглавие + опциите му в role="group" — четецът казва
      // „Услуги (602), група", а стрелките вървят през всички подред.
      o.sections(items, q).filter((sec) => sec && sec.items && sec.items.length).forEach((sec, k) => {
        const hid = uid + '-g' + k;
        box.appendChild(el('div', { class: 'combo-group', role: 'group', 'aria-labelledby': hid },
          el('div', { class: 'combo-group-title', id: hid, role: 'presentation', text: sec.label }),
          ...sec.items.map(opt)));
      });
    } else {
      items.forEach((it) => box.appendChild(opt(it)));
    }
    if (!items.length) box.appendChild(el('div', { class: 'combo-empty', text: o.emptyText || 'Няма съвпадение.' }));
    if (found.length > items.length) {
      box.appendChild(el('div', { class: 'combo-empty', text: 'Още ' + (found.length - items.length) + ' — напишете още няколко букви.' }));
    }
    if (o.footer) {
      const i = shown.length;
      const text = String(inp.value || '').trim();
      shown.push({ footer: true });
      box.appendChild(el('div', {
        class: 'combo-opt combo-footer', role: 'option', id: uid + '-o' + i, 'aria-selected': 'false',
        onMousedown: (e) => { e.preventDefault(); choose(i); },
      }, o.footer.label(text)));
    }
    setActive(shown.length && (items.length || o.footerDefault) ? 0 : -1);
  }

  // Телефонът (под 768 px) с mobileFull — там CSS-ът слага всичко на цял
  // екран и плаващите размери биха му пречили.
  const phoneFull = () => !!(o.mobileFull && typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 767px)').matches);
  function place() {
    if (!o.float || !box || !open) return;
    if (!inp.isConnected) { hide(); return; }
    const st = box.style;
    if (phoneFull()) { st.position = st.left = st.top = st.bottom = st.width = st.maxHeight = st.zIndex = ''; return; }
    const r = inp.getBoundingClientRect();
    const vw = window.innerWidth || 1024, vh = window.innerHeight || 768;
    const w = Math.min(Math.max(r.width, 300), vw - 16);
    const below = vh - r.bottom - 8, above = r.top - 8;
    // нагоре само ако долу няма място, а горе има повече
    const up = below < 240 && above > below;
    st.position = 'fixed';
    st.zIndex = '150';
    st.width = w + 'px';
    st.left = Math.max(8, Math.min(r.left, vw - w - 8)) + 'px';
    st.maxHeight = Math.max(140, Math.min(380, up ? above : below)) + 'px';
    st.top = up ? '' : (r.bottom + 2) + 'px';
    st.bottom = up ? (vh - r.top + 2) + 'px' : '';
  }
  function show() {
    if (inp.disabled) return;
    const was = open;
    open = true;
    render();
    root.classList.add('combo-open');
    box.style.display = '';
    inp.setAttribute('aria-expanded', 'true');
    if (o.float && !was && typeof window !== 'undefined') {
      window.addEventListener('scroll', place, true);
      window.addEventListener('resize', place);
    }
    place();
  }
  function hide() {
    if (o.float && open && typeof window !== 'undefined') {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    }
    open = false;
    root.classList.remove('combo-open');
    if (box) box.style.display = 'none';
    inp.setAttribute('aria-expanded', 'false');
    inp.removeAttribute('aria-activedescendant');
  }

  function choose(i) {
    const s = shown[i];
    if (!s) return;
    if (s.footer) {
      const text = String(inp.value || '').trim();
      hide();
      showPicked();
      o.footer.onPick(text);
      return;
    }
    const changed = !picked || String(idOf(picked)) !== String(idOf(s.item));
    picked = s.item;
    showPicked();
    hide();
    if (changed && o.onPick) o.onPick(picked);
  }

  inp.addEventListener('focus', () => { if (inp.select) inp.select(); });
  inp.addEventListener('click', () => { if (!open) show(); });
  inp.addEventListener('input', () => { show(); });
  inp.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { show(); return; }
      if (shown.length) setActive((active + 1) % shown.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { show(); return; }
      if (shown.length) setActive((active - 1 + shown.length) % shown.length);
    } else if (e.key === 'Enter') {
      if (!open) return;
      e.preventDefault();
      if (active >= 0) choose(active);
    } else if (e.key === 'Escape') {
      if (!open) return;
      // Esc затваря само списъка — не и диалога, в който стои полето.
      e.preventDefault();
      e.stopPropagation();
      hide();
      showPicked();
    }
  });
  inp.addEventListener('blur', () => {
    const wasOpen = open;
    hide();
    const text = String(inp.value || '').trim();
    if (text === '') {
      if (picked) { picked = null; if (o.onPick) o.onPick(null); }
      inp.value = '';
      return;
    }
    const typed = !picked || inp.value !== labelOf(picked);
    if (typed && (o.autoPickSingle || o.onBlurUnmatched)) {
      const m = matches(text);
      // Едно-единствено съвпадение: човек явно има предвид него.
      if (o.autoPickSingle && m.length === 1) {
        picked = m[0];
        showPicked();
        if (o.onPick) o.onPick(picked);
        return;
      }
      if (!m.length && o.onBlurUnmatched) { showPicked(); o.onBlurUnmatched(text); return; }
    }
    if (wasOpen || !picked || inp.value !== labelOf(picked)) showPicked();
  });

  picked = find(o.value);
  showPicked();
  if (o.disabled) inp.disabled = true;

  root.input = inp;
  root.getValue = () => (picked ? idOf(picked) : null);
  root.getItem = () => picked;
  // Смяна на избора отвън (без onPick — викащият вече знае).
  root.setValue = (id) => { picked = find(id); showPicked(); };
  root.setDisabled = (on) => { inp.disabled = !!on; if (on) hide(); };
  root.open = () => { inp.focus(); show(); };
  return root;
}
